import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import spinnerGif from '../../assets/spinner-tv.gif';
import './HubLoadingMessage.css';

interface HubLoadingMessageProps {
  active: boolean;
  scope: string;
}

function phaseFromElapsed(elapsedMs: number): number {
  if (elapsedMs >= 60_000) return 3;
  if (elapsedMs >= 30_000) return 2;
  if (elapsedMs >= 10_000) return 1;
  return 0;
}

export const HubLoadingMessage: React.FC<HubLoadingMessageProps> = ({ active, scope }) => {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!active) {
      setPhase(0);
      return;
    }

    const storageKey = `ott_hub_loading_started:${scope}:${i18n.language}`;
    const maxAgeMs = 10 * 60 * 1000;
    const now = Date.now();
    let startedAt = now;

    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? Number(raw) : 0;
      if (Number.isFinite(parsed) && parsed > 0 && now - parsed < maxAgeMs) {
        startedAt = parsed;
      } else {
        localStorage.setItem(storageKey, String(startedAt));
      }
    } catch {
      startedAt = now;
    }

    const updatePhase = () => setPhase(phaseFromElapsed(Date.now() - startedAt));
    updatePhase();
    const interval = window.setInterval(updatePhase, 1000);
    return () => window.clearInterval(interval);
  }, [active, scope, i18n.language]);

  useEffect(() => {
    if (active) return;
    try {
      localStorage.removeItem(`ott_hub_loading_started:${scope}:${i18n.language}`);
    } catch {}
  }, [active, scope, i18n.language]);

  if (!active) return null;

  return (
    <div className="hub-loading-message" aria-live="polite">
      <div className="hub-loading-message__panel">
        <img className="hub-loading-message__spinner" src={spinnerGif} alt="" />
        <div className="hub-loading-message__copy">
          <div className="hub-loading-message__title">{t(`contentList.matchedLoading.phase${phase}.title`)}</div>
          <div className="hub-loading-message__body">{t(`contentList.matchedLoading.phase${phase}.body`)}</div>
        </div>
      </div>
    </div>
  );
};

export default HubLoadingMessage;
